// ChatConversation.js - Version avec ChatOrchestrator intelligent et analyse IA complète

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ChatOrchestrator from './ChatOrchestrator';
import ChatIntentAnalyzer from './ChatIntentAnalyzer';

// Utilitaires de conversation
export const conversationUtilities = {
  createUserMessage: (content, options = {}) => ({
    id: Date.now(),
    role: 'user',
    content: content.trim(),
    timestamp: new Date().toISOString(),
    ...options
  }),

  createAssistantMessage: (content, context = {}) => ({
    id: Date.now() + 1,
    role: 'assistant',
    content,
    timestamp: new Date().toISOString(),
    context,
    isError: false
  }),

  createErrorMessage: (content) => ({
    id: Date.now() + 1,
    role: 'assistant',
    content,
    timestamp: new Date().toISOString(),
    isError: true
  }),

  formatConversationContext: (userObjective, currentStep, sourcesCount) => {
    const context = [];
    if (userObjective) context.push(`Objectif: ${userObjective}`);
    if (currentStep) context.push(`Étape: ${currentStep.titre}`);
    if (sourcesCount > 0) context.push(`${sourcesCount} sources disponibles`);
    return context.join(' | ');
  },

  isRecentMessage: (timestamp) => {
    const messageTime = new Date(timestamp);
    const now = new Date();
    return (now - messageTime) < 5 * 60 * 1000;
  },

  // ⚠️ DEPRECATED - Remplacé par analyzeComprehensive() de ChatIntentAnalyzer
  // Conservé pour compatibilité temporaire
  detectAdvancement: (messageContent) => {
    console.warn('⚠️ detectAdvancement() est déprécié. Utilisez ChatIntentAnalyzer.analyzeComprehensive()');
    return { detected: false };
  }
};

// Hook principal avec SAUVEGARDE FIREBASE
export const useChatConversation = ({
  userObjective,
  savedSources,
  actionPlan,
  currentStepId,
  competenceData,
  onUpdateStepResults,
  selectedTool = 'auto'  // ✨ NOUVEAU : Mode de traitement sélectionné
}) => {
  
  const [chatMessages, setChatMessages] = useState([]);
  const [historicalMessages, setHistoricalMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [conversationContext, setConversationContext] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  
  const [pendingAdvancements, setPendingAdvancements] = useState([]);
  const [confirmedAdvancements, setConfirmedAdvancements] = useState([]);
  const [pendingValidation, setPendingValidation] = useState(null); 
  const [comprehensiveAnalysis, setComprehensiveAnalysis] = useState(null); // ✨ NOUVEAU : Dernière analyse
  
  const activeRequestRef = useRef(null);
  const conversationIdRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasLoadedRef = useRef(false); // 🔥 NOUVEAU : pour éviter le chargement automatique
  
  const currentStepData = useMemo(() => {
    if (!actionPlan?.steps || !currentStepId) return null;
    return actionPlan.steps.find(step => step.id === currentStepId);
  }, [actionPlan, currentStepId]);
  
  const contextualInfo = useMemo(() => {
    return conversationUtilities.formatConversationContext(
      userObjective,
      currentStepData,
      savedSources?.length || 0
    );
  }, [userObjective, currentStepData, savedSources]);
  
  const allMessages = useMemo(() => {
    return [...historicalMessages, ...chatMessages];
  }, [historicalMessages, chatMessages]);

  // 🔥 NOUVELLE FONCTION : Extraire les notes d'étape depuis competenceData
  const getStepNotes = useCallback((stepId) => {
    if (!competenceData?.stepNotes?.[stepId]) {
      return {
        decisions: '',
        questions: '',
        resources: '',
        ai_recommendations: ''
      };
    }
    
    const notes = competenceData.stepNotes[stepId];
    
    // Extraire le contenu textuel des notes (sans le formatage)
    const extractTextContent = (noteContent) => {
      if (!noteContent) return '';
      
      // Supprimer les marqueurs de mémo (###)
      let text = noteContent.replace(/###\s*[^\n]+\n/g, '');
      
      // Supprimer les métadonnées (Date, Catégorie, Source)
      text = text.replace(/\*\*Date:\*\*[^\n]+\n/g, '');
      text = text.replace(/\*\*Catégorie:\*\*[^\n]+\n/g, '');
      text = text.replace(/\*\*Source:\*\*[^\n]+\n/g, '');
      text = text.replace(/---\n/g, '');
      
      // Nettoyer les espaces multiples
      text = text.replace(/\n\n+/g, '\n').trim();
      
      return text;
    };
    
    return {
      decisions: extractTextContent(notes.decisions) || '',
      questions: extractTextContent(notes.questions) || '',
      resources: extractTextContent(notes.resources) || '',
      ai_recommendations: extractTextContent(notes.ai_recommendations) || ''
    };
  }, [competenceData]);

  // 🔥 Fonction helper pour calculer la complétion d'étape
  const calculateStepCompletion = useCallback(() => {
    if (!confirmedAdvancements.length) return {
      percentage: 0,
      advancementCount: 0,
      isComplete: false,
      isNearComplete: false,
      missingPercentage: 100
    };
    
    const stepAdvancements = confirmedAdvancements.filter(
      adv => adv.metadata?.stepId === currentStepId
    );
    const totalPercentage = stepAdvancements.reduce((sum, adv) => sum + (adv.percentage || 0), 0);
    const advancementCount = stepAdvancements.length;
    const isComplete = totalPercentage >= 100 || advancementCount >= 5;
    const isNearComplete = totalPercentage >= 80 || advancementCount >= 4;
    
    return {
      percentage: Math.min(100, totalPercentage),
      advancementCount,
      isComplete,
      isNearComplete,
      missingPercentage: Math.max(0, 100 - totalPercentage)
    };
  }, [confirmedAdvancements, currentStepId]);

  // 🔥 NOUVELLE FONCTION : Récupérer les notes de TOUTES les étapes (historique complet)
  const getAllStepsNotes = useCallback(() => {
    if (!competenceData?.stepNotes || !actionPlan?.steps) {
      return [];
    }
    
    return actionPlan.steps
      .map(step => {
        const notes = getStepNotes(step.id);
        const hasNotes = notes.decisions || notes.questions || notes.resources || notes.ai_recommendations;
        
        if (!hasNotes) return null;
        
        return {
          stepId: step.id,
          stepTitle: step.titre,
          stepOrder: step.order || actionPlan.steps.indexOf(step) + 1,
          notes: notes,
          isCurrentStep: step.id === currentStepId,
          isCompleted: step.id !== currentStepId // Les étapes avant la courante sont considérées complétées
        };
      })
      .filter(stepData => stepData !== null)
      .sort((a, b) => a.stepOrder - b.stepOrder); // Trier par ordre chronologique
  }, [competenceData, actionPlan, currentStepId, getStepNotes]);

  // 🔥 Fonction utilitaire pour nettoyer les valeurs undefined (Firebase ne les accepte pas)
  const cleanUndefined = useCallback((obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(cleanUndefined).filter(item => item !== null);
    
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          const cleanedValue = cleanUndefined(value);
          if (cleanedValue !== null && Object.keys(cleanedValue).length > 0) {
            cleaned[key] = cleanedValue;
          }
        } else {
          cleaned[key] = value;
        }
      }
    });
    return Object.keys(cleaned).length > 0 ? cleaned : null;
  }, []);

  // 🔥 Sauvegarde automatique dans Firebase
  const saveConversationToFirebase = useCallback(async (messages, forceImmediate = false) => {
    const currentUser = auth.currentUser;
    
    if (!currentUser?.uid || !competenceData?.id || !messages || messages.length === 0) {
      console.log('Conditions non remplies pour sauvegarde conversation');
      return false;
    }

    if (!forceImmediate) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        saveConversationToFirebase(messages, true);
      }, 2000);
      return;
    }

    try {
      console.log('💾 Sauvegarde conversation dans Firebase...');
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', competenceData.id);
      
      // Filtrer les messages de validation temporaires et nettoyer les undefined
      const messagesToSave = messages
        .filter(msg => msg.type !== 'progress_validation') // Exclure les cartes de validation
        .map(msg => cleanUndefined({
          id: msg.id,
          role: msg.role,
          content: msg.content || '',
          timestamp: msg.timestamp,
          context: msg.context || {},
          isError: msg.isError || false,
          isSynthesis: msg.context?.isSynthesis || false
        }))
        .filter(msg => msg !== null); // Supprimer les messages null après nettoyage
      
      const conversationData = cleanUndefined({
        messages: messagesToSave,
        lastUpdate: new Date().toISOString(),
        messageCount: messagesToSave.length,
        conversationId: conversationIdRef.current || null,
        currentStepId: currentStepId || null
      });
      
      console.log(`📝 Sauvegarde de ${messagesToSave.length} messages (${messages.length - messagesToSave.length} cartes de validation exclues)`);

      await updateDoc(docRef, {
        conversationHistory: conversationData,
        conversationUpdatedAt: serverTimestamp()
      });

      if (isMountedRef.current) {
        setLastSavedAt(new Date());
      }
      
      console.log('✅ Conversation sauvegardée:', messagesToSave.length, 'messages');
      return true;
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde conversation:', error);
      console.error('Détails:', error.message);
      return false;
    }
  }, [competenceData, currentStepId, cleanUndefined]);

  // 🔥 MODIFICATION : Restauration MANUELLE (pas automatique)
  const loadConversationFromFirebase = useCallback(async () => {
    const currentUser = auth.currentUser;
    
    if (!currentUser?.uid || !competenceData?.id) {
      return [];
    }

    try {
      console.log('🔥 Chargement conversation depuis Firebase...');
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', competenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const savedConversation = data.conversationHistory;
        
        if (savedConversation && savedConversation.messages && savedConversation.messages.length > 0) {
          console.log('✅ Conversation trouvée:', savedConversation.messages.length, 'messages');
          
          // 🔥 MODIFICATION : Placer directement dans l'historique au lieu de chatMessages
          if (isMountedRef.current) {
            setHistoricalMessages(savedConversation.messages);
            if (savedConversation.conversationId) {
              conversationIdRef.current = savedConversation.conversationId;
            }
          }
          
          return savedConversation.messages;
        } else {
          console.log('ℹ️ Aucune conversation sauvegardée trouvée');
        }
      }
      
      return [];
      
    } catch (error) {
      console.error('❌ Erreur chargement conversation:', error);
      return [];
    }
  }, [competenceData]);

  // 🔥 MODIFICATION : Ne PAS charger automatiquement au montage
  // useEffect(() => {
  //   loadConversationFromFirebase();
  // }, [loadConversationFromFirebase]);
  
  // 🔥 NOUVEAU : Charger UNIQUEMENT si demandé explicitement
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadConversationFromFirebase();
      hasLoadedRef.current = true;
    }
  }, [loadConversationFromFirebase]);

  // Sauvegarder automatiquement à chaque changement de messages
  useEffect(() => {
    const allMessages = [...historicalMessages, ...chatMessages];
    if (allMessages.length > 0) {
      saveConversationToFirebase(allMessages);
    }
  }, [chatMessages, historicalMessages, saveConversationToFirebase]);

  // Fonction de confirmation d'avancement
  const confirmAdvancement = useCallback(async (advancementId, customPercentage = null) => {
    const advancement = pendingAdvancements.find(adv => adv.id === advancementId);
    if (!advancement) return false;

    try {
      const confirmedAdvancement = {
        id: `confirmed_${Date.now()}`,
        type: 'chat_detected',
        category: advancement.type,
        content: advancement.extractedContent,
        percentage: customPercentage || advancement.suggestedPercentage,
        points: calculatePoints(advancement.type),
        timestamp: new Date().toISOString(),
        source: 'chat_conversation',
        metadata: {
          originalMessageId: advancement.messageId,
          detectionConfidence: advancement.confidence,
          userConfirmed: true,
          stepId: currentStepId
        }
      };

      if (onUpdateStepResults && currentStepId) {
        const success = await onUpdateStepResults(currentStepId, confirmedAdvancement);
        
        if (success) {
          setPendingAdvancements(prev => prev.filter(adv => adv.id !== advancementId));
          setConfirmedAdvancements(prev => [...prev, confirmedAdvancement]);
          
          setChatMessages(prev => prev.map(msg => 
            msg.id === advancement.messageId 
              ? { ...msg, confirmedAdvancement: confirmedAdvancement }
              : msg
          ));
          
          console.log('✅ Avancement confirmé et enregistré');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('❌ Erreur confirmation avancement:', error);
      setError('Erreur lors de la confirmation de l\'avancement');
      return false;
    }
  }, [pendingAdvancements, currentStepId, onUpdateStepResults]);

  const dismissAdvancement = useCallback((advancementId) => {
    setPendingAdvancements(prev => prev.filter(adv => adv.id !== advancementId));
    
    const advancement = pendingAdvancements.find(adv => adv.id === advancementId);
    if (advancement) {
      setChatMessages(prev => prev.map(msg => 
        msg.id === advancement.messageId 
          ? { ...msg, dismissedAdvancement: true }
          : msg
      ));
    }
  }, [pendingAdvancements]);

  const calculatePoints = useCallback((type) => {
    const pointsMap = {
      'completion': 30,
      'discovery': 20,
      'decision': 25,
      'learning': 15,
      'progress': 10,
      'obstacle_solved': 35
    };
    return pointsMap[type] || 15;
  }, []);
  
  // Fonction d'envoi de message
  const sendMessage = useCallback(async (messageContent, options = {}) => {
    if (!messageContent.trim() || isLoading) return false;
    
    setIsLoading(true);
    setError(null);
    
    if (activeRequestRef.current) {
      activeRequestRef.current.cancelled = true;
    }
    
    const currentRequest = { cancelled: false };
    activeRequestRef.current = currentRequest;
    
    try {
      if (options.isSynthesisOnly) {
        const assistantMessage = conversationUtilities.createAssistantMessage(messageContent, {
          sourcesCount: savedSources?.length || 0,
          currentStepTitle: currentStepData?.titre,
          conversationId: conversationIdRef.current,
          isSynthesis: true
        });
        
        setChatMessages(prev => [...prev, assistantMessage]);
        return true;
      }
      
      // ✨ NOUVEAU : Analyse complète et contextuelle par IA
      const intentAnalyzer = new ChatIntentAnalyzer();
      
      console.log('🔍 Préparation de l\'analyse avec contexte:', {
        objectif: userObjective,
        etapeCourante: currentStepData?.titre,
        sourcesCount: savedSources?.length || 0,
        avancementsCount: confirmedAdvancements?.length || 0
      });
      
      const analysis = await intentAnalyzer.analyzeComprehensive(messageContent, {
        objective: userObjective ? { titre: userObjective } : null,
        currentStep: currentStepData,
        allSteps: actionPlan?.steps || [],
        selectedSources: savedSources,
        stepNotes: getStepNotes(currentStepId),
        allStepsNotes: getAllStepsNotes(), // ✨ NOUVEAU : Historique complet des décisions
        confirmedAdvancements: confirmedAdvancements,
        conversationHistory: chatMessages.slice(-10)
      });
      
      console.log('📊 Analyse complète reçue:', {
        intentType: analysis.intent?.type,
        avancementDetecte: analysis.progress?.avancement_detecte,
        etapeConcernee: analysis.progress?.etape_concernee,
        contenu: analysis.progress?.contenu?.substring(0, 100)
      });
      setComprehensiveAnalysis(analysis);
      
      // 🔄 GÉRER LES RÉPÉTITIONS
      if (analysis.context.isRepetition) {
        const repetitionMessage = conversationUtilities.createAssistantMessage(
          `Je vois que vous avez déjà posé une question similaire. ${analysis.context.previousAnswer}\n\nVoulez-vous que je développe un aspect particulier ?`,
          { isRepetition: true }
        );
        setChatMessages(prev => [...prev, repetitionMessage]);
        setIsLoading(false);
        return true;
      }
      
      const userMessage = conversationUtilities.createUserMessage(messageContent, {
        ...options,
        analysis: analysis // ✨ Inclure l'analyse complète
      });
      setChatMessages(prev => [...prev, userMessage]);
      
      // ✅ GÉRER LES AVANCEMENTS DÉTECTÉS
      if (analysis.progress?.avancement_detecte && currentStepData) {
        console.log('✅ Avancement détecté ! Création de la carte de validation...');
        
        // Créer une carte de validation qui sera affichée par le composant parent
        const validationCard = {
          id: `validation_${Date.now()}`,
          role: 'system',
          type: 'progress_validation',
          analysis: analysis,
          timestamp: new Date().toISOString()
        };
        
        setPendingValidation(validationCard);
        
        // Ajouter la carte AVANT le message utilisateur pour qu'elle soit visible
        setChatMessages(prev => [...prev, validationCard]);
        
        console.log('📋 Carte de validation ajoutée aux messages');
        
      } else {
        console.log('ℹ️ Aucun avancement détecté dans cette requête');
      }
      
      setConversationContext(contextualInfo);
      
      // ✨ NOUVEAU : Utiliser ChatOrchestrator pour traiter le message intelligemment
      const orchestrator = new ChatOrchestrator();
      
      // Préparer le contexte pour l'orchestrateur
      const orchestratorContext = {
        userId: auth.currentUser?.uid,
        competenceId: competenceData?.id,
        objective: userObjective ? { titre: userObjective } : null,
        currentStep: currentStepData,
        selectedSources: savedSources,
        actionPlan,
        
        // ✨ NOUVEAU : Inclure l'analyse complète
        analysis: analysis,
        
        // ✨ NOUVEAU : Inclure les notes et l'historique complet
        stepNotes: getStepNotes(currentStepId),
        allStepsNotes: getAllStepsNotes(),
        confirmedAdvancements: confirmedAdvancements,
        
        // ✨ NOUVEAU : Inclure les résultats accumulés
        stepResults: {
          confirmedAdvancements: confirmedAdvancements,
          stepNotes: getStepNotes(currentStepId),
          completionPercentage: calculateStepCompletion()
        },
        
        conversationHistory: chatMessages.slice(-5).map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      };
      
      // Options pour l'orchestrateur
      const orchestratorOptions = {
        forceTool: selectedTool !== 'auto' ? selectedTool : null,
        generateRecommendations: options.generateRecommendations,
        intent: analysis.intent // ✨ NOUVEAU : Passer l'intention détectée
      };
      
      const orchestratorResult = await orchestrator.processQuery(
        messageContent,
        orchestratorContext,
        orchestratorOptions
      );
      
      if (currentRequest.cancelled) {
        return false;
      }
      
      // Créer le message assistant avec les métadonnées de l'orchestrateur
      const assistantMessage = conversationUtilities.createAssistantMessage(
        orchestratorResult.answer,
        {
          sourcesCount: savedSources?.length || 0,
          currentStepTitle: currentStepData?.titre,
          conversationId: conversationIdRef.current,
          toolUsed: orchestratorResult.source,
          toolLabel: orchestratorResult.sourceLabel,
          intentAnalysis: orchestratorResult.intent,
          model: orchestratorResult.model,
          usage: orchestratorResult.usage,
          cost: orchestratorResult.cost,
          citations: orchestratorResult.citations,
          relatedQuestions: orchestratorResult.relatedQuestions, // ✨ NOUVEAU : Questions connexes de Perplexity
          documents: orchestratorResult.documents,
          synthesis: orchestratorResult.synthesis || null,
          processingTime: orchestratorResult.processingTime,
          
          // ✨ NOUVEAU : Inclure l'analyse complète et les recommandations
          analysis: analysis,
          suggestedQuestions: analysis.recommendations.suggestedNextQuestions
        }
      );
      
      setChatMessages(prev => [...prev, assistantMessage]);
      
      return true;
      
    } catch (error) {
      if (currentRequest.cancelled) {
        return false;
      }
      
      console.error('Erreur envoi message:', error);
      setError('Erreur lors de l\'envoi du message: ' + error.message);
      
      const errorMessage = conversationUtilities.createErrorMessage(
        'Désolé, une erreur s\'est produite lors du traitement de votre message.'
      );
      setChatMessages(prev => [...prev, errorMessage]);
      
      return false;
      
    } finally {
      setIsLoading(false);
      if (activeRequestRef.current === currentRequest) {
        activeRequestRef.current = null;
      }
    }
  }, [
    isLoading,
    userObjective,
    savedSources,
    currentStepData,
    contextualInfo,
    currentStepId,
    selectedTool,
    chatMessages,
    actionPlan
  ]);
  
  const clearCurrentChat = useCallback(() => {
    setChatMessages([]);
    setError(null);
    setConversationContext('');
    setPendingAdvancements([]);
    setConfirmedAdvancements([]);
    conversationIdRef.current = null;
    
    // Sauvegarder la suppression dans Firebase
    saveConversationToFirebase([], true);
  }, [saveConversationToFirebase]);
  
  // 🔥 MODIFICATION : startFreshConversation sauvegarde d'abord avant de vider
  const startFreshConversation = useCallback(async () => {
    // Sauvegarder l'état actuel AVANT de vider pour éviter la perte de messages
    const currentMessages = [...historicalMessages, ...chatMessages];
    if (currentMessages.length > 0) {
      await saveConversationToFirebase(currentMessages, true);
    }

    // Déplacer les messages actuels vers l'historique et vider
    setHistoricalMessages(currentMessages);
    setChatMessages([]);
    setError(null);
    setConversationContext('');
    setPendingAdvancements([]);
    setConfirmedAdvancements([]);
    conversationIdRef.current = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, [saveConversationToFirebase, chatMessages, historicalMessages]);
  
  useEffect(() => {
    if (!conversationIdRef.current) {
      conversationIdRef.current = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }, []);
  
  useEffect(() => {
    const newContext = contextualInfo;
    if (newContext !== conversationContext) {
      setConversationContext(newContext);
    }
  }, [contextualInfo, conversationContext]);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (activeRequestRef.current) {
        activeRequestRef.current.cancelled = true;
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  return {
    chatMessages,
    historicalMessages,
    allMessages,
    isLoading,
    error,
    connectionStatus,
    conversationContext,
    currentStepData,
    contextualInfo,
    lastSavedAt,
    
    hasMessages: chatMessages.length > 0,
    hasHistoricalMessages: historicalMessages.length > 0,
    messageCount: chatMessages.length,
    totalMessageCount: allMessages.length,
    conversationId: conversationIdRef.current,
    isReady: connectionStatus === 'connected',
    
    sendMessage,
    clearCurrentChat,
    startFreshConversation,
    setChatMessages,
    
    setError,
    setConnectionStatus,
    
    pendingAdvancements,
    confirmedAdvancements,
    confirmAdvancement,
    dismissAdvancement,
    hasPendingAdvancements: pendingAdvancements.length > 0,
    
    pendingValidation,
    setPendingValidation,
    comprehensiveAnalysis,
    
    saveConversationToFirebase,
    loadConversationFromFirebase
  };
};
